import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin } from 'lucide-react';
import { studioInfo } from '../data/mock';

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-white via-yellow-50 to-pink-50 border-t-4 border-yellow-300 text-gray-800 pt-16 pb-8">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <div>
            <img 
              src="/logo.png" 
              alt="Creative Minds Logo"
              className="h-28 w-auto object-contain mb-4"
            />
            <p className="text-gray-600 text-sm leading-relaxed">
              {studioInfo.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Quick Links</h4>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-gray-600 hover:text-pink-600 transition-colors text-sm" data-testid="footer-link-home">
                Home
              </Link>
              <Link to="/portfolio" className="text-gray-600 hover:text-amber-600 transition-colors text-sm" data-testid="footer-link-portfolio">
                Portfolio
              </Link>
              <Link to="/events" className="text-gray-600 hover:text-purple-600 transition-colors text-sm" data-testid="footer-link-book-now">
                Book Now
              </Link>
              <Link to="/testimonials" className="text-gray-600 hover:text-blue-600 transition-colors text-sm" data-testid="footer-link-testimonials">
                Testimonials
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-pink-600 transition-colors text-sm" data-testid="footer-link-contact">
                Contact
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Contact Us</h4>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${studioInfo.email}`} className="flex items-center gap-2 text-gray-600 hover:text-pink-600 transition-colors text-sm">
                <Mail className="w-4 h-4 text-pink-500" />
                {studioInfo.email}
              </a>
              <a href={`tel:${studioInfo.phone}`} className="flex items-center gap-2 text-gray-600 hover:text-amber-600 transition-colors text-sm">
                <Phone className="w-4 h-4 text-amber-500" />
                {studioInfo.phone}
              </a>
              <div className="flex items-start gap-2 text-gray-600 text-sm">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-purple-500" />
                <span>{studioInfo.address}</span>
              </div>
            </div>
          </div>

          {/* Hours & Social */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Hours</h4>
            <div className="text-gray-600 text-sm mb-2">
              <p className="mb-1">Mon-Fri: {studioInfo.hours.weekday}</p>
              <p className="mb-1">Sat-Sun: {studioInfo.hours.weekend}</p>
              <p className="text-pink-600 mt-2 font-semibold">{studioInfo.hours.note}</p>
            </div>
            <div className="flex gap-4 mt-6">
              <a 
                href="https://www.instagram.com/creativemindsfargo/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-pink-400 to-yellow-400 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61583622694458"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-400 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://www.tiktok.com/@creativemindsfargo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="TikTok"
                data-testid="footer-social-tiktok"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-pink-200 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              ©2026 Creative Minds. All rights reserved
            </p>
            <Link 
              to="/admin" 
              className="text-gray-400 hover:text-gray-600 text-xs transition-colors"
            >
              Admin Access
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
